import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";
import appConfig from "../../data/config";
import { apiFetch } from "../../helpers/apiFetch";
import { resetProductCache } from "./product-slice";
import { clearCart } from "./cart-slice";
import { clearCupon } from "./cupon-slice";

export const registerUser = createAsyncThunk(
  "auth/register",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await fetch(`${appConfig.apiUrl}/auth/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        // Laravel returns validation errors as { errors: { field: [msg] } }
        if (data.errors) {
          const first = Object.values(data.errors)[0][0];
          return rejectWithValue({ message: first, errors: data.errors });
        }
        return rejectWithValue({ message: data.message || "Error al registrarse", errors: null });
      }

      return data; // { token, user }
    } catch {
      return rejectWithValue("Error de conexión. Intente nuevamente.");
    }
  }
);

export const loginUser = createAsyncThunk(
  "auth/login",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${appConfig.apiUrl}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      if (response.status === 429) {
        const retryAfter = parseInt(response.headers.get("Retry-After"), 10) || 60;
        return rejectWithValue({ rateLimited: true, retryAfter });
      }

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue({ message: data.message || "Credenciales inválidas" });
      }

      return data; // { token, user }
    } catch {
      return rejectWithValue({ message: "Error de conexión. Intente nuevamente." });
    }
  }
);

// POST /auth/social-login
export const socialLogin = createAsyncThunk(
  'auth/socialLogin',
  async ({ provider, token }, { rejectWithValue }) => {
    try {
      const res = await fetch(`${appConfig.apiUrl}/auth/social-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ provider, token }),
      });
      const data = await res.json();
      if (!res.ok) return rejectWithValue(data.message || 'Error al autenticar con ' + provider);
      return data;
    } catch {
      return rejectWithValue('Error de conexión.');
    }
  }
);

// POST /auth/completar-registro
// Último paso del alta por Google/Facebook: el usuario cargó nombre y email vía la red
// social y acá completa los datos obligatorios. El backend devuelve { user } con
// perfil_incompleto=false, que es lo que libera el resto del sitio (ver PerfilIncompletoGate).
export const completarRegistro = createAsyncThunk(
  'auth/completarRegistro',
  async (formData, { rejectWithValue, getState }) => {
    const { auth } = getState();
    try {
      const res = await apiFetch(`${appConfig.apiUrl}/auth/completar-registro`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${auth.token}`,
        },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.errors) {
          return rejectWithValue({ message: Object.values(data.errors)[0][0], errors: data.errors });
        }
        return rejectWithValue({ message: data.message || 'No pudimos completar tu registro.', errors: null });
      }
      return data; // { user }
    } catch {
      return rejectWithValue({ message: 'Error de conexión. Intente nuevamente.', errors: null });
    }
  }
);

// POST /auth/forgot-password
export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async ({ email }, { rejectWithValue }) => {
    try {
      const res = await fetch(`${appConfig.apiUrl}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) return rejectWithValue(data.message || 'Error');
      return data.message;
    } catch {
      return rejectWithValue('Error de conexión.');
    }
  }
);

// POST /auth/reset-password
export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ token, email, password, password_confirmation }, { rejectWithValue }) => {
    try {
      const res = await fetch(`${appConfig.apiUrl}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ token, email, password, password_confirmation }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.errors) return rejectWithValue(Object.values(data.errors)[0][0]);
        return rejectWithValue(data.message || 'Error');
      }
      return data.message;
    } catch {
      return rejectWithValue('Error de conexión.');
    }
  }
);

// PATCH /auth/change-password
export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async ({ current_password, password, password_confirmation }, { rejectWithValue, getState }) => {
    const { auth } = getState();
    try {
      const res = await apiFetch(`${appConfig.apiUrl}/auth/change-password`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${auth.token}`,
        },
        body: JSON.stringify({ current_password, password, password_confirmation }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.errors) return rejectWithValue(Object.values(data.errors)[0][0]);
        return rejectWithValue(data.message || 'Error');
      }
      return data.message;
    } catch {
      return rejectWithValue('Error de conexión.');
    }
  }
);

// PUT /auth/profile
export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (profileData, { rejectWithValue, getState }) => {
    const { auth } = getState();
    try {
      const res = await apiFetch(`${appConfig.apiUrl}/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${auth.token}`,
        },
        body: JSON.stringify(profileData),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.errors) return rejectWithValue(Object.values(data.errors)[0][0]);
        return rejectWithValue(data.message || 'Error');
      }
      return data;
    } catch {
      return rejectWithValue('Error de conexión.');
    }
  }
);

// Logout completo: revoca token en el server, limpia auth + cart + product cache + cupon.
// Usar este thunk en vez del reducer `logout` cuando se necesita cleanup completo
// (expiración de sesión, click manual en logout, 401 en apiFetch).
export const logoutAndCleanup = createAsyncThunk(
  'auth/logoutAndCleanup',
  async (_, { dispatch, getState }) => {
    const { auth } = getState();

    if (auth.token) {
      try {
        await fetch(`${appConfig.apiUrl}/auth/logout`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${auth.token}`,
            Accept: 'application/json',
          },
        });
      } catch {
        // server revoke failed (network or already-revoked) — no bloquea el cleanup local
      }
    }

    dispatch(authSlice.actions.logout());
    dispatch(resetProductCache());
    dispatch(clearCart());
    dispatch(clearCupon());

    return null;
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: null,
    token: null,
    // Solo viene presente en la respuesta de /auth/login y /auth/me cuando
    // el usuario logueado es viajante (role === 'viajante'). null para
    // cualquier otro rol.
    permisos: null,
    loading: false,
    error: null,
    loginRateLimitedUntil: null,
    passwordLoading: false,
    passwordError: null,
    passwordSuccess: null,
  },
  reducers: {
    logout(state) {
      state.user = null;
      state.token = null;
      state.error = null;
      localStorage.removeItem('dp_auth');
    },
    // Usado por /impersonate: setea la sesión con un token ya emitido por el
    // backend (POST /admin/users/{id}/impersonate) en vez de pasar por loginUser.
    setSession(state, action) {
      state.token = action.payload.token;
      state.user = action.payload.user;
      state.permisos = action.payload.permisos ?? null;
      state.error = null;
      localStorage.setItem('dp_auth', JSON.stringify({ token: action.payload.token, user: action.payload.user, permisos: action.payload.permisos ?? null }));
    },
    // Actualiza user + permisos con la respuesta de GET /auth/me — usado en la
    // rehidratación de sesión al refrescar la página (StorageSync en App.js).
    // No toca el token: ya fue validado contra el server antes de llamar acá.
    refreshUserFromMe(state, action) {
      state.user = action.payload.user;
      state.permisos = action.payload.permisos ?? null;
      localStorage.setItem('dp_auth', JSON.stringify({ token: state.token, user: action.payload.user, permisos: action.payload.permisos ?? null }));
    },
    clearError(state) {
      state.error = null;
    },
    clearPasswordState(state) {
      state.passwordLoading = false;
      state.passwordError = null;
      state.passwordSuccess = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.loginRateLimitedUntil = null;
        state.token = action.payload.token;
        state.user = action.payload.user;
        // Solo viene presente cuando el usuario logueado es viajante.
        state.permisos = action.payload.permisos ?? null;
        localStorage.setItem('dp_auth', JSON.stringify({ token: action.payload.token, user: action.payload.user, permisos: action.payload.permisos ?? null }));
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        if (payload && payload.rateLimited) {
          state.loginRateLimitedUntil = Date.now() + payload.retryAfter * 1000;
          state.error = null;
        } else {
          state.loginRateLimitedUntil = null;
          state.error = (payload && typeof payload === 'object') ? payload.message : payload;
        }
      })
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.user = action.payload.user;
        localStorage.setItem('dp_auth', JSON.stringify({ token: action.payload.token, user: action.payload.user }));
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        const p = action.payload;
        state.error = (p && typeof p === 'object') ? p.message : p;
      })
      .addCase(socialLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(socialLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.user = action.payload.user;
        localStorage.setItem('dp_auth', JSON.stringify({ token: action.payload.token, user: action.payload.user }));
      })
      .addCase(socialLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(completarRegistro.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(completarRegistro.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        localStorage.setItem('dp_auth', JSON.stringify({ token: state.token, user: action.payload.user, permisos: state.permisos ?? null }));
      })
      .addCase(completarRegistro.rejected, (state, action) => {
        state.loading = false;
        const p = action.payload;
        state.error = (p && typeof p === 'object') ? p.message : p;
      })
      .addCase(updateProfile.pending, (state) => {
        state.passwordLoading = true;
        state.passwordError = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.user = action.payload;
        localStorage.setItem('dp_auth', JSON.stringify({ token: state.token, user: action.payload }));
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.passwordLoading = false;
        state.passwordError = action.payload;
      })
      .addCase(changePassword.pending, (state) => {
        state.passwordLoading = true;
        state.passwordError = null;
        state.passwordSuccess = null;
      })
      .addCase(changePassword.fulfilled, (state, action) => {
        state.passwordLoading = false;
        state.passwordSuccess = action.payload;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.passwordLoading = false;
        state.passwordError = action.payload;
      })
      .addCase(forgotPassword.pending, (state) => {
        state.passwordLoading = true;
        state.passwordError = null;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.passwordLoading = false;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.passwordLoading = false;
        state.passwordError = action.payload;
      })
      .addCase(resetPassword.pending, (state) => {
        state.passwordLoading = true;
        state.passwordError = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.passwordLoading = false;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.passwordLoading = false;
        state.passwordError = action.payload;
      })
      // Datos viejos en localStorage (de antes de que auth se sacara del blacklist
      // general) pueden traer un error/loginRateLimitedUntil obsoleto en el rehydrate.
      // Al tocar este substate acá, el stateReconciler de redux-persist detecta que
      // "ya fue modificado" y no lo pisa con el valor stale del storage.
      .addCase(REHYDRATE, (state) => {
        state.loading = false;
        state.error = null;
        state.loginRateLimitedUntil = null;
      });
  },
});

export const { logout, clearError, clearPasswordState, setSession, refreshUserFromMe } = authSlice.actions;
export default authSlice.reducer;
